/**
 * Registro de hilos de mensajes abiertos en pantalla.
 *
 * POR QUÉ EXISTE. El realtime de WhatsApp preguntaba "¿este mensaje es de la conversación
 * SELECCIONADA?" — una sola, la de la bandeja. Mientras el chat vivía nada más que ahí, alcanzaba.
 * Dejó de alcanzar el día que el mismo hilo se pudo montar en tres ventanas acopladas y en el
 * menú del encabezado: los mensajes que llegaban a un chat que no era "el seleccionado" no
 * actualizaban nada, así que un mensaje enviado desde el dock se quedaba con un solo check para
 * siempre aunque el cliente ya lo hubiera leído.
 *
 * Ahora la pregunta es otra: "¿hay ALGÚN hilo de esta conversación en pantalla?". Cada
 * `useWaMessages` se anota acá al montarse y se borra al desmontarse; el realtime le entrega los
 * cambios a quien corresponda, sean uno o cuatro.
 *
 * 🪤 ES ESTADO A NIVEL DE MÓDULO, NO UN CONTEXTO, Y ES A PROPÓSITO. Quien escribe en el registro
 * es un manejador de eventos de Supabase, no un componente: pasarlo por props o contexto obligaría
 * a re-suscribir el canal cada vez que cambia una referencia. Mismo criterio que la caché de
 * mensajes de `useWaMessages`, que ya vive así.
 *
 * 🪤 UNA CONVERSACIÓN PUEDE TENER VARIOS HILOS A LA VEZ (la bandeja abierta y una ventana acoplada
 * del mismo cliente). Por eso el valor es un conjunto y no un único suscriptor: quedarse con el
 * último dejaría al otro congelado.
 */

import type { WaMessage } from '@/components/whatsapp/WaMessageThread';

interface HiloAbierto {
  setMessages: React.Dispatch<React.SetStateAction<WaMessage[]>>;
  /** Para bajar el scroll cuando entra un mensaje. Puede no existir todavía. */
  finDelHilo?: React.RefObject<HTMLDivElement | null>;
}

const hilos = new Map<string, Set<HiloAbierto>>();

/** Anota un hilo. Devuelve la función que lo da de baja — llamala al desmontar. */
export function registrarHilo(conversationId: string, hilo: HiloAbierto): () => void {
  const actuales = hilos.get(conversationId) ?? new Set<HiloAbierto>();
  actuales.add(hilo);
  hilos.set(conversationId, actuales);

  return () => {
    const set = hilos.get(conversationId);
    if (!set) return;
    set.delete(hilo);
    if (set.size === 0) hilos.delete(conversationId);
  };
}

/** ¿Hay al menos un hilo de esta conversación en pantalla? */
export function hayHiloAbierto(conversationId: string | null | undefined): boolean {
  return !!conversationId && (hilos.get(conversationId)?.size ?? 0) > 0;
}

/** Aplica un cambio a todos los hilos de esta conversación. Si no hay ninguno, no hace nada. */
export function aplicarAlHilo(
  conversationId: string,
  actualizar: React.SetStateAction<WaMessage[]>,
): void {
  const set = hilos.get(conversationId);
  if (!set) return;
  for (const h of set) h.setMessages(actualizar);
}

/** Baja el scroll de los hilos de esta conversación, si están montados. */
export function bajarScrollDelHilo(conversationId: string): void {
  const set = hilos.get(conversationId);
  if (!set) return;
  for (const h of set) {
    // El timeout deja que React pinte el mensaje nuevo antes de medir el alto.
    setTimeout(() => h.finDelHilo?.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }
}

/** Qué conversaciones están abiertas en pantalla ahora mismo. */
export function conversacionesConHiloAbierto(): string[] {
  return [...hilos.entries()].filter(([, s]) => s.size > 0).map(([id]) => id);
}


/** Solo para tests: deja el registro limpio entre casos. */

export function _limpiarRegistroDeHilos(): void {
  hilos.clear();
}
